import translate from '@translate';
import React, { FC, useEffect, useState } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { OrderType } from 'src/types/order';
import { OnChangeMultipleType } from '.';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Typography from 'src/components/Typography';
import { UserServiceCode } from 'src/types/user';
import { ProviderType } from 'src/types/provider';
import Switch from 'src/components/Switch';
import Tooltip from 'src/components/Tooltip';
import { IconButton } from '@mui/material';
import AddressIdSelect from './AddressIdSelect';
import { ExportIcon, FileCountryIcon, FileCountryReturnIcon } from 'src/components/SkladonIcon/SmallIcons';
import AutocompleteTextField from 'src/components/AutocompleteTextField';
import { sizes } from 'src/themes/default';
import { InfoIcon } from '../../components/SkladonIcon/Icons';
import { styled } from '@mui/material/styles';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import { isOrderBoxCountEditDisabled } from './functions';
import api from '@api';
import EgonBadge from 'src/components/EgonBadge';
import { useCheckPermission } from 'src/utils/checkPermission';
import { useArchiveMode } from 'src/utils/archiveMode';

export interface TransportPropsType {
    onChange: OnChangeType<EditKeysType>;
    onChangeMultiple: OnChangeMultipleType;
    data: OrderType | null;
    editData: OrderType;
    editErrors: FormErrorsType;
    newOrder: boolean;
    disableEdit: boolean;
    permissionEditAll: boolean;
    permissionManagerEdit: boolean;
    permissionEditOrderBoxCount: boolean;
    isAttributeEditDisabled: (attributeKey: string) => boolean;
    shipperCodes: ConstantsCodeType[];
    serviceCodes: ConstantsCodeType[];
    providerServiceCodes: UserServiceCode[];
    handleShowShipperLabel: (value: 'shipResponse' | 'shipReturnResponse' | 'shipSwapResponse') => void;
    summary?: boolean;
    providerData?: ProviderType | null;
    skladonUser?: number;
}

export const dhlSortedCodes = [
    'DHLESU',
    'DHLESIDDP',
    'DHLESIDAP',
    'DHLECX',
    'DHLWPXDAP',
    'DHLWPXDDP',
    'DHLD2SVPECX',
    'DHLD2SVPWPXDAP',
    'DHLD2SVPWPXDDP',
    'DHLD2SVPESU',
    'DHLD2SVPESIDAP',
    'DHLD2SVPESIDDP',
];
const InfoIconStyled = styled('span')({
    marginLeft: '10px',

    '& svg': {
        width: '14px',
        height: '14px',
        marginRight: '10px'
    }
});

const Transport: FC<TransportPropsType> = (props) => {
    const permissionEgonView = useCheckPermission('EGON.VIEW');
    const showEgonBadge =
        !!props.skladonUser && permissionEgonView && (props.data ?? props.editData).SHIPPING_BACKEND === 'egon';
    const archive = useArchiveMode();
    const [shipperCodes, setShipperCodes] = useState<ConstantsCodeType[]>([]);
    const [serviceCodes, setServiceCodes] = useState<ConstantsCodeType[]>([]);
    useEffect(() => {
        if (props.editData.SHIPPER && shipperCodes) {
            const orderType = (props.editData.ORDER_TYPE || '').toLowerCase();
            const filteredServiceCodes = props.serviceCodes.filter((serviceCode) => {
                const matchesShipper = serviceCode.shipperCode === props.editData.SHIPPER;
                const matchesProvider = !!props.providerServiceCodes.find(
                    (providerServiceCode) => providerServiceCode.VALUE === serviceCode.value
                );
                if (!matchesShipper || !matchesProvider) {
                    return false;
                }

                const hasParcelPalletFlags =
                    serviceCode.isParcel !== undefined || serviceCode.isPallet !== undefined;
                if (hasParcelPalletFlags) {
                    if (orderType === 'b2c') {
                        return !!serviceCode.isParcel;
                    }
                    if (orderType === 'b2b') {
                        return !!serviceCode.isParcel || !!serviceCode.isPallet;
                    }
                }

                // Fallback for constants without IS_PARCEL / IS_PALLET yet
                return !!serviceCode.isExpedition;
            });

            // dhl needs different sorting
            if (props.editData.SHIPPER === 'DHL') {
                const newCodes: ConstantsCodeType[] = [];
                dhlSortedCodes.forEach((code) => {
                    const found = filteredServiceCodes.find((filteredCode) => filteredCode.value === code);
                    // push only those that provider has access to
                    if (found) {
                        newCodes.push(found);
                    }
                });
                setServiceCodes(newCodes);
            } else {
                // Local Courier (value: PCKT_EXTERNAL): sort alphabetically by label (A -> Z)
                const isLocalCourier = props.editData.SHIPPER === 'PCKT_EXTERNAL';

                setServiceCodes(
                    isLocalCourier
                        ? [...filteredServiceCodes].sort((a, b) => (a.label || '').localeCompare(b.label || ''))
                        : filteredServiceCodes
                );
            }

        } else {
            setServiceCodes([]);
        }
    }, [props.editData.SHIPPER, props.editData.ORDER_TYPE, shipperCodes, props.serviceCodes, props.providerServiceCodes]);

    useEffect(() => {
        if (props.shipperCodes && props.providerServiceCodes) {
            setShipperCodes(
                props.shipperCodes.filter((shipperCode) =>
                    props.providerServiceCodes.find(
                        (providerServiceCode) => providerServiceCode.SHIPPER_CODE === shipperCode.value
                    )
                )
            );
        }
    }, [props.shipperCodes, props.providerServiceCodes]);

    useEffect(() => {
        if (serviceCodes.length === 1) {
            if (props.editData.SHIPPER_SERVICE_CODE !== serviceCodes[0].value) {
                props.onChange('SHIPPER_SERVICE_CODE', serviceCodes[0].value, false);
            }
        } else if (serviceCodes.length > 1) {
            if (props.editData.SHIPPER_SERVICE_CODE) {
                const isCurrentServiceValid = serviceCodes.some(
                    (serviceCode) => serviceCode.value === props.editData.SHIPPER_SERVICE_CODE
                );
                if (!isCurrentServiceValid) {
                    props.onChange('SHIPPER_SERVICE_CODE', undefined, false);
                }
            }
        }
        // Don't clear SHIPPER_SERVICE_CODE when serviceCodes is empty - it might be temporarily empty during tab switching
    }, [serviceCodes, props.editData.SHIPPER_SERVICE_CODE]);

    // Při změně dopravce/služby mažeme return a exchange TN (doručovací TN se přepíše při přeobjednání, logiku necháváme)
    const clearReturnAndExchangeTrackingForShipperChange = (): Partial<OrderType> => ({
        RETURN_TRACKING_NUMBER: '',
        EXCHANGE_TRACKING_NUMBER: ''
    });

    const onChangeShipper = (value: any) => {
        const newData: OrderType = {
            SHIPPER: value,
            SHIPPER_SERVICE_CODE: undefined,
            ADDRESS_ID: '',
            BRANCH_ID: null,
            ...clearReturnAndExchangeTrackingForShipperChange()
        };
        if (value === 'VLASTNI') {
            newData.TRACKING_NUMBER = 'vlastni';
        }
        props.onChangeMultiple(newData);
    };

    const onChangeShipperServiceCode = (value: any) => {
        const newData: OrderType = {
            SHIPPER_SERVICE_CODE: value,
            // Při změně služby dopravce je stávající výdejní místo neplatné
            // (přechod z výdejního místa na doručení na adresu i mezi různými typy výdejních míst),
            // proto ID výdejního místa vždy mažeme, aby objednávka neskončila v chybě.
            ADDRESS_ID: '',
            BRANCH_ID: null,
            ...clearReturnAndExchangeTrackingForShipperChange()
        };
        props.onChangeMultiple(newData);
    };

    const EXCHANGE_SERVICE_CODES = ['GLSXS', 'GLSSKXS', 'DPDXS'];
    const isExchangeServiceCode = EXCHANGE_SERVICE_CODES.includes(
        props.editData.SHIPPER_SERVICE_CODE || props.data?.SHIPPER_SERVICE_CODE || ''
    );

    const downloadFileData = async (type: string, idOrder: number) => {
        let url = `api/secure/orders/shipper-label/${idOrder}`;
        if (type === 'shipReturnResponse') {
            url = `api/secure/orders/shipper-return-label/${idOrder}`;
        } else if (type === 'shipSwapResponse') {
            url = `api/secure/orders/shipper-exchange-label/${idOrder}`;
        }

        const response: { data: { label: string } } = await api('get', url);
        let base64Data = response.data.label;

        // Pokud base64 řetězec ještě neobsahuje prefix, přidáme ho
        if (base64Data.indexOf(';base64,') === -1) {
            base64Data = `data:application/pdf;base64,${base64Data}`;
        }

        // Vytvoření dočasného odkazu pro stažení
        const downloadLink = document.createElement('a');
        downloadLink.href = base64Data;

        // Nastavení názvu souboru
        const fileName = `label-${idOrder}.pdf`;
        downloadLink.download = fileName;

        // Přidání do DOM, kliknutí a odstranění
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    let widthTrackingNumber = 0;
    if (!props.newOrder) {
        widthTrackingNumber += 46;
    }

    const orderFiles = props.editData.FILES ?? props.data?.FILES;

    const showReturnLabel =
        !archive &&
        !props.newOrder &&
        props.providerData?.ENABLE_RETURN_LABELS === 1 &&
        !!props.editData.PRINT_RETURN_LABEL &&
        !!orderFiles?.find((file) => file.type === 'shipReturnResponse');

    const showExchangeLabel =
        !archive &&
        !props.newOrder &&
        isExchangeServiceCode &&
        !!orderFiles?.find((file) => file.type === 'shipSwapResponse');

    const showLabel =
        !archive &&
        props.editData.STATUS !== 'productsNotAvailable' &&
        !props.newOrder &&
        !!orderFiles?.find((file) => file.type === 'shipResponse');

    const branchIdEnabled =
        props.data?.PROVIDER?.BRANCH_ID_ENABLED === 1 || props.providerData?.BRANCH_ID_ENABLED === 1;

    return (
        <Grid>
            <GridItem cols={12}>
                <Typography variant="h3" gutterBottom>
                    {translate('orderDetail.transport')}
                    {showEgonBadge && (
                        <span style={{ marginLeft: 12, verticalAlign: 'middle' }}>
                            <EgonBadge
                                orderId={(props.data ?? props.editData).EGON_ORDER_ID}
                                statusName={(props.data ?? props.editData).EGON_STATUS_NAME}
                                statusAt={(props.data ?? props.editData).EGON_STATUS_AT}
                                data-testid="order-egon-badge"
                            />
                        </span>
                    )}
                </Typography>
            </GridItem>
            <GridItem cols={branchIdEnabled ? 3 : 5}>
                <Select
                    options={props.shipperCodes}
                    editableOptions={shipperCodes.filter((option): boolean => {
                        if (option.value === 'notSupported' && props.editData.SHIPPER !== 'notSupported') return false;
                        // if (option.value === 'CUSTOM' && props.editData.SHIPPER !== 'CUSTOM') return false;
                        return true;
                    })}
                    value={props.editData.SHIPPER}
                    error={props.editErrors.SHIPPER}
                    onChange={onChangeShipper}
                    name="SHIPPER"
                    label={translate('orderDetail.shipper')}
                    required
                    fullWidth
                    disabled={(props.isAttributeEditDisabled('SHIPPER') && !props.permissionManagerEdit) || props.summary}
                />
            </GridItem>
            <GridItem cols={5}>
                <Select
                    options={props.serviceCodes}
                    editableOptions={serviceCodes}
                    value={props.editData.SHIPPER_SERVICE_CODE}
                    error={props.editErrors.SHIPPER_SERVICE_CODE}
                    onChange={onChangeShipperServiceCode}
                    name="SHIPPER_SERVICE_CODE"
                    label={translate('orderDetail.shipperService')}
                    required
                    fullWidth
                    disabled={(props.isAttributeEditDisabled('SHIPPER_SERVICE_CODE') && !props.permissionManagerEdit) || props.summary}
                />
            </GridItem>
            <GridItem cols={2}>
                <TextField
                    value={props.editData.ORDER_BOX_COUNT}
                    error={props.editErrors.ORDER_BOX_COUNT}
                    onChange={(value, error) =>
                        props.onChange('ORDER_BOX_COUNT', value === undefined ? 1 : value, error)
                    }
                    name="ORDER_BOX_COUNT"
                    label={translate('orderDetail.boxCount')}
                    fullWidth
                    type="numberLocale"
                    min={1}
                    step="1"
                    disabled={isOrderBoxCountEditDisabled({
                        permissionEditAll: props.permissionEditAll,
                        permissionManagerEdit: props.permissionManagerEdit,
                        permissionEditOrderBoxCount: props.permissionEditOrderBoxCount,
                        summary: props.summary,
                    })}
                    clearValueUndefined
                />
            </GridItem>
            {branchIdEnabled && (
                <GridItem cols={2}>
                    <TextField
                        value={props.editData.BRANCH_ID ?? ''}
                        error={props.editErrors.BRANCH_ID}
                        onChange={(value, error) => props.onChange('BRANCH_ID', value || null, error)}
                        name="BRANCH_ID"
                        label={translate('orderDetail.branchId')}
                        fullWidth
                        maxLength={100}
                        disabled={props.disableEdit || props.summary}
                        inputProps={{ 'data-testid': 'order-branch-id' }}
                    />
                </GridItem>
            )}
            <AddressIdSelect
                onChange={props.onChange}
                editData={props.editData}
                editErrors={props.editErrors}
                disableEdit={props.disableEdit}
                summary={props.summary}
            />
            <GridItem cols={4}>
                <TextField
                    value={props.editData.STATUS === 'productsNotAvailable' ? '' : props.editData.TRACKING_NUMBER}
                    error={props.editErrors.TRACKING_NUMBER}
                    onChange={(value, error) => props.onChange('TRACKING_NUMBER', value, error)}
                    name="TRACKING_NUMBER"
                    label={translate('orderDetail.trackingNumber')}
                    fullWidth
                    disabled={(props.isAttributeEditDisabled('TRACKING_NUMBER') && !props.permissionManagerEdit) || props.summary || props.editData.STATUS === 'productsNotAvailable'}
                    sx={{ width: `calc(100% - ${2 * widthTrackingNumber}px)` }}
                />
                {showLabel && (
                    <>
                        <Tooltip title={translate('orderDetail.shipperLabel')}>
                            <IconButton
                                onClick={() => props.handleShowShipperLabel('shipResponse')}
                                size="small"
                                data-testid="order-detail-shipper-label-btn"
                                sx={{
                                    margin: sizes.icon_button_next_to_input_MG,
                                    '& svg': {
                                        width: sizes.icon_button_next_to_input_svg_size,
                                        height: sizes.icon_button_next_to_input_svg_size
                                    }
                                }}
                            >
                                <FileCountryIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={translate('orderDetail.shipperLabelDownload')}>
                            <IconButton
                                onClick={() => downloadFileData('shipResponse', props.editData.ID || 0)}
                                size="small"
                                data-testid="order-detail-shipper-label-download-btn"
                                sx={{
                                    margin: sizes.icon_button_next_to_input_MG,
                                    '& svg': {
                                        width: sizes.icon_button_next_to_input_svg_size,
                                        height: sizes.icon_button_next_to_input_svg_size
                                    }
                                }}
                            >
                                <ExportIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </GridItem>
            <GridItem cols={4}>
                <TextField
                    value={props.editData.STATUS === 'productsNotAvailable' ? '' : props.editData.RETURN_TRACKING_NUMBER}
                    error={props.editErrors.RETURN_TRACKING_NUMBER}
                    onChange={(value, error) => props.onChange('RETURN_TRACKING_NUMBER', value, error)}
                    name="RETURN_TRACKING_NUMBER"
                    label={translate('orderDetail.returnTrackingNumber')}
                    fullWidth
                    disabled={!props.permissionEditAll || props.summary || props.editData.STATUS === 'productsNotAvailable'}
                    sx={{ width: `calc(100% - ${2 * widthTrackingNumber}px)` }}
                />
                {showReturnLabel && props.editData.STATUS !== 'productsNotAvailable' && (
                    <>
                        <Tooltip title={translate('orderDetail.shipperReturnLabel')}>
                            <IconButton
                                onClick={() => props.handleShowShipperLabel('shipReturnResponse')}
                                size="small"
                                sx={{
                                    margin: sizes.icon_button_next_to_input_MG,
                                    '& svg': {
                                        width: sizes.icon_button_next_to_input_svg_size,
                                        height: sizes.icon_button_next_to_input_svg_size
                                    }
                                }}
                            >
                                <FileCountryReturnIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={translate('orderDetail.shipperReturnLabel')}>
                            <IconButton
                                onClick={() => downloadFileData('shipReturnResponse', props.editData.ID || 0)}
                                size="small"
                                sx={{
                                    margin: sizes.icon_button_next_to_input_MG,
                                    '& svg': {
                                        width: sizes.icon_button_next_to_input_svg_size,
                                        height: sizes.icon_button_next_to_input_svg_size
                                    }
                                }}
                            >
                                <ExportIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </GridItem>
            {isExchangeServiceCode && (
                <GridItem cols={4}>
                    <TextField
                        value={props.editData.STATUS === 'productsNotAvailable' ? '' : props.editData.EXCHANGE_TRACKING_NUMBER}
                        error={props.editErrors.EXCHANGE_TRACKING_NUMBER}
                        onChange={(value, error) => props.onChange('EXCHANGE_TRACKING_NUMBER', value, error)}
                        name="EXCHANGE_TRACKING_NUMBER"
                        label={translate('orderDetail.exchangeTrackingNumber')}
                        fullWidth
                        disabled={!props.permissionEditAll || props.summary || props.editData.STATUS === 'productsNotAvailable'}
                        sx={{ width: `calc(100% - ${2 * widthTrackingNumber}px)` }}
                    />
                    {showExchangeLabel && props.editData.STATUS !== 'productsNotAvailable' && (
                        <>
                            <Tooltip title={translate('orderDetail.shipperExchangeLabel')}>
                                <IconButton
                                    onClick={() => props.handleShowShipperLabel('shipSwapResponse')}
                                    size="small"
                                    sx={{
                                        margin: sizes.icon_button_next_to_input_MG,
                                        '& svg': {
                                            width: sizes.icon_button_next_to_input_svg_size,
                                            height: sizes.icon_button_next_to_input_svg_size
                                        }
                                    }}
                                >
                                    <FileCountryReturnIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={translate('orderDetail.shipperExchangeLabel')}>
                                <IconButton
                                    onClick={() => downloadFileData('shipSwapResponse', props.editData.ID || 0)}
                                    size="small"
                                    sx={{
                                        margin: sizes.icon_button_next_to_input_MG,
                                        '& svg': {
                                            width: sizes.icon_button_next_to_input_svg_size,
                                            height: sizes.icon_button_next_to_input_svg_size
                                        }
                                    }}
                                >
                                    <ExportIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </GridItem>
            )}
            <GridItem cols={4}>
                <AutocompleteTextField
                    value={props.editData.MESSAGE}
                    error={props.editErrors.MESSAGE}
                    onChange={(value, error) => props.onChange('MESSAGE', value || '', error)}
                    name="MESSAGE"
                    label={<>
                        {translate('orderDetail.message')}
                        <Tooltip title={translate('orderDetail.message.tooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>} fullWidth
                    multiline
                    disabled={props.disableEdit || props.summary}
                    autoComplete="ORDER_MESSAGE"
                    maxLength={35}
                />
            </GridItem>
            <GridItem cols={6}>
                <Switch
                    value={!!props.editData.TRANSPORT_PAID_BY_SKLADON}
                    onChange={(value) => props.onChange('TRANSPORT_PAID_BY_SKLADON', value ? 1 : 0, false)}
                    label={translate('orderDetail.transportPaidBySkladon')}
                    disabled={!(props.permissionEditAll || props.permissionManagerEdit) || props.summary}
                />
            </GridItem>
            <GridItem cols={6}>
                <Switch
                    value={!!props.editData.SHIPMENT_PENDING}
                    disabled
                    label={translate('orderDetail.shipmentPending')}
                />
            </GridItem>
            {props.providerData?.ENABLE_RETURN_LABELS === 1 && (
                <GridItem cols={6}>
                    <Switch
                        value={!!(props.editData.PRINT_RETURN_LABEL ?? props.providerData?.PRINT_RETURN_LABEL)}
                        onChange={(value) => props.onChange('PRINT_RETURN_LABEL', value ? 1 : 0, false)}
                        label={translate('orderDetail.printReturnLabel')}
                        disabled={props.disableEdit || props.summary}
                    />
                </GridItem>
            )}
            {props.editData.ORDER_TYPE?.toLowerCase() === 'b2b' && (
                <GridItem cols={6}>
                    <Switch
                        value={!!props.editData.HOLD_BY_QUALITY}
                        onChange={(value) => props.onChange('HOLD_BY_QUALITY', value ? 1 : 0, false)}
                        disabled={props.disableEdit || props.summary}
                        label={translate('orderDetail.holdByQuality')}
                    />
                </GridItem>
            )}
            {props.providerData?.ALLOW_DISABLE_SHIPPING_REORDER === 1 && (
                <GridItem cols={6}>
                    <Switch
                        value={!!props.editData.DISABLE_SHIPPING_REORDER}
                        onChange={(value) => props.onChange('DISABLE_SHIPPING_REORDER', value ? 1 : 0, false)}
                        disabled={!props.newOrder || props.summary}
                        label={translate('orderDetail.disableShippingReorder')}
                    />
                </GridItem>
            )}
            {props.providerData?.CUSTOMS_WAREHOUSE_ENABLED === 1 && (
                <GridItem cols={6}>
                    <Switch
                        value={!!props.editData.PICK_FROM_CUSTOMS_WAREHOUSE}
                        onChange={(value) => props.onChange('PICK_FROM_CUSTOMS_WAREHOUSE', value ? 1 : 0, false)}
                        disabled={props.disableEdit || props.summary}
                        label={translate('orderDetail.pickFromCustomsWarehouse')}
                    />
                </GridItem>
            )}
        </Grid>
    );
};

export default Transport;
